// ./src/utils/api/WeatherService.ts
//
// Live weather forecast for a location (the event's venue). Mirrors the
// eventsmagic-api `weatherForecast` query — hourly + daily arrays in metric. We
// request it fresh on every dashboard visit / reload (no client caching), so the
// organizer always sees the latest outlook.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

const FORECAST_QUERY = gql`
  query WeatherForecast($latitude: Float, $longitude: Float, $hours: Int, $days: Int) {
    weatherForecast(latitude: $latitude, longitude: $longitude, hours: $hours, days: $days) {
      code
      data {
        timezone
        hours {
          time
          isDaytime
          tempC
          feelsLikeC
          conditionCode
          description
          iconBaseUri
          precipProbabilityPct
          precipMm
          windSpeedKmh
          humidityPct
          uvIndex
        }
        days {
          date
          tempMinC
          tempMaxC
          conditionCode
          description
          iconBaseUri
          precipProbabilityPct
          precipMm
          sunriseTime
          sunsetTime
        }
      }
      errors { name errors warnings }
    }
  }
`;

const POLLEN_QUERY = gql`
  query Pollen($latitude: Float, $longitude: Float, $days: Int) {
    pollen(latitude: $latitude, longitude: $longitude, days: $days) {
      code
      data {
        days {
          date
          types {
            code
            displayName
            index
            category
            inSeason
          }
        }
      }
      errors { name errors warnings }
    }
  }
`;

const AIR_QUALITY_QUERY = gql`
  query AirQuality($latitude: Float, $longitude: Float, $hours: Int) {
    airQuality(latitude: $latitude, longitude: $longitude, hours: $hours) {
      code
      data {
        aqi
        category
        dominantPollutant
        hours {
          time
          aqi
          category
        }
      }
      errors { name errors warnings }
    }
  }
`;

export interface WeatherForecastHour {
  time?: string | null;
  isDaytime?: boolean | null;
  tempC?: number | null;
  feelsLikeC?: number | null;
  conditionCode?: string | null;
  description?: string | null;
  iconBaseUri?: string | null;
  precipProbabilityPct?: number | null;
  precipMm?: number | null;
  windSpeedKmh?: number | null;
  humidityPct?: number | null;
  uvIndex?: number | null;
}

export interface WeatherForecastDay {
  date?: string | null;
  tempMinC?: number | null;
  tempMaxC?: number | null;
  conditionCode?: string | null;
  description?: string | null;
  iconBaseUri?: string | null;
  precipProbabilityPct?: number | null;
  precipMm?: number | null;
  sunriseTime?: string | null;
  sunsetTime?: string | null;
}

export interface WeatherForecast {
  timezone?: string | null;
  hours?: WeatherForecastHour[] | null;
  days?: WeatherForecastDay[] | null;
}

export interface AirQualityHour {
  time?: string | null;
  aqi?: number | null;
  category?: string | null;
}

export interface AirQuality {
  aqi?: number | null;
  category?: string | null;
  dominantPollutant?: string | null;
  hours?: AirQualityHour[] | null;
}

export interface PollenType {
  code?: string | null; // GRASS | TREE | WEED
  displayName?: string | null;
  index?: number | null; // 0–5
  category?: string | null;
  inSeason?: boolean | null;
}

export interface PollenDay {
  date?: string | null;
  types?: PollenType[] | null;
}

export interface Pollen {
  days?: PollenDay[] | null;
}

export class WeatherService {
  static async forecast(latitude: number, longitude: number, hours?: number, days?: number): Promise<OpResult> {
    return ApiService.executeQuery(FORECAST_QUERY, { latitude, longitude, hours, days });
  }

  static async airQuality(latitude: number, longitude: number, hours?: number): Promise<OpResult> {
    return ApiService.executeQuery(AIR_QUALITY_QUERY, { latitude, longitude, hours });
  }

  static async pollen(latitude: number, longitude: number, days?: number): Promise<OpResult> {
    return ApiService.executeQuery(POLLEN_QUERY, { latitude, longitude, days });
  }
}
